import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { can } from "@/lib/permissions";
import { PageHeader, DataTable, EmptyState, ConfirmDialog } from "@/components/ui-system";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PlugZap,
  Plug,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  RefreshCw,
  FileKey,
  History,
} from "lucide-react";

// White-label aggregators. Identity is admin-only; sellers never see these names.
const AGGREGATORS = [
  { value: "shiprocket", label: "Shiprocket" },
  { value: "ithink", label: "iThink Logistics" },
];

const aggregatorLabel = (name) =>
  AGGREGATORS.find((a) => a.value === name)?.label || name;

const errorDetail = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return error?.message || fallback;
};

const relativeTime = (iso) => {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return null;
  const diffMs = ts - Date.now();
  const abs = Math.abs(diffMs);
  const qty = abs < 3600000 ? `${Math.round(abs / 60000)}m`
    : abs < 86400000 ? `${Math.round(abs / 3600000)}h`
    : `${Math.round(abs / 86400000)}d`;
  return diffMs >= 0 ? `in ${qty}` : `${qty} ago`;
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

/**
 * Read-only integration view. B2C aggregator credentials are managed via
 * environment variables (backend/.env) — see docs/B2C_STAGE_C_CREDENTIALS.md —
 * not through this page. The page surfaces connection + token health and lets an
 * admin run a (read-only) Test Connection.
 */
const B2CPartners = ({ user, onLogout }) => {
  const canManage = can(user, "b2c.partners:manage");

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [savingPriorityId, setSavingPriorityId] = useState(null);
  const [pendingToggle, setPendingToggle] = useState(null); // { partner, next }
  const [toggling, setToggling] = useState(false);
  const [historyPartner, setHistoryPartner] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/b2c/admin/partners`);
      setPartners(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(errorDetail(err, "Failed to load partners"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const handleTestConnection = async (partner) => {
    setTestingId(partner.id);
    setTestResults((prev) => ({ ...prev, [partner.id]: undefined }));
    try {
      const res = await axios.post(`${API}/b2c/admin/partners/${partner.id}/test-connection`);
      const result = { ok: Boolean(res.data?.ok), message: res.data?.message || "" };
      setTestResults((prev) => ({ ...prev, [partner.id]: result }));
      if (result.ok) toast.success(result.message || "Connection OK");
      else toast.error(result.message || "Connection failed");
    } catch (err) {
      const message = errorDetail(err, "Connection test failed");
      setTestResults((prev) => ({ ...prev, [partner.id]: { ok: false, message } }));
      toast.error(message);
    } finally {
      setTestingId(null);
    }
  };

  const applyToggle = async () => {
    if (!pendingToggle) return;
    const { partner, next } = pendingToggle;
    setToggling(true);
    try {
      await axios.patch(`${API}/b2c/admin/partners/${partner.id}/enabled`, { enabled: next });
      toast.success(
        `${partner.display_name || aggregatorLabel(partner.name)} ${next ? "enabled" : "disabled"}`
      );
      setPendingToggle(null);
      await fetchPartners();
    } catch (err) {
      toast.error(errorDetail(err, "Failed to update partner status"));
    } finally {
      setToggling(false);
    }
  };

  const openHistory = async (partner) => {
    setHistoryPartner(partner);
    setHistory([]);
    setHistoryError(null);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/b2c/admin/partners/${partner.id}/enable-history`);
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const message = errorDetail(err, "Failed to load history");
      setHistoryError(message);
      toast.error(message);
    } finally {
      setHistoryLoading(false);
    }
  };

  // -- cell renderers -------------------------------------------------------

  const renderEnvironment = (p) => (
    <Badge className={`border-0 ${p.environment === "production" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
      {p.environment === "production" ? "Production" : "Staging"}
    </Badge>
  );

  // Enabled is DB-managed: the toggle PATCHes b2c_partners and records an audit
  // row. The flip is gated behind a confirm dialog (live-booking warning on
  // enable). The Switch stays bound to p.enabled, so it only moves after a
  // confirmed PATCH + refetch — cancelling leaves it where it was.
  const renderStatus = (p) => {
    const busy = toggling && pendingToggle?.partner?.id === p.id;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Switch
          checked={!!p.enabled}
          onCheckedChange={(next) => setPendingToggle({ partner: p, next })}
          disabled={!canManage || busy}
          data-testid={`partner-toggle-${p.name}`}
          aria-label={`${p.enabled ? "Disable" : "Enable"} ${aggregatorLabel(p.name)}`}
        />
        {p.enabled ? (
          <Badge className="border-0 bg-green-100 text-green-800" data-testid={`partner-enabled-${p.name}`}>
            <CheckCircle2 className="mr-1 h-3 w-3" /> Live
          </Badge>
        ) : (
          <Badge className="border-0 bg-slate-100 text-slate-600" data-testid={`partner-enabled-${p.name}`}>
            Disabled
          </Badge>
        )}
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
        <button
          type="button"
          onClick={() => openHistory(p)}
          className="inline-flex items-center gap-0.5 text-xs text-brand hover:underline"
          data-testid={`partner-history-${p.name}`}
        >
          <History className="h-3 w-3" /> History
        </button>
      </div>
    );
  };

  const renderCredentials = (p) => {
    if (p.credentials_source === "env") {
      return (
        <Badge className="border-0 bg-green-100 text-green-800" data-testid={`partner-creds-${p.name}`}>
          <FileKey className="mr-1 h-3 w-3" /> Managed via .env
        </Badge>
      );
    }
    if (p.credentials_configured) {
      return (
        <Badge className="border-0 bg-green-100 text-green-800" data-testid={`partner-creds-${p.name}`}>
          <CheckCircle2 className="mr-1 h-3 w-3" /> Configured
        </Badge>
      );
    }
    return (
      <Badge className="border-0 bg-slate-100 text-slate-500" data-testid={`partner-creds-${p.name}`}>
        <XCircle className="mr-1 h-3 w-3" /> Not set
      </Badge>
    );
  };

  // Token health — only meaningful for shiprocket (ithink has no token).
  const renderTokenHealth = (p) => {
    if (p.name !== "shiprocket") return <span className="text-xs text-slate-400">n/a</span>;
    if (p.token_healthy) {
      return (
        <Badge className="border-0 bg-green-100 text-green-800" data-testid={`partner-token-${p.name}`}>
          <ShieldCheck className="mr-1 h-3 w-3" /> Healthy
        </Badge>
      );
    }
    const rel = relativeTime(p.token_expires_at);
    return (
      <Badge className="border-0 bg-amber-100 text-amber-800" data-testid={`partner-token-${p.name}`}>
        <ShieldAlert className="mr-1 h-3 w-3" /> {p.token_expires_at ? `Expiring ${rel}` : "No token yet"}
      </Badge>
    );
  };

  const renderActions = (p) => {
    const result = testResults[p.id];
    return (
      <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-center md:justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleTestConnection(p)}
          disabled={!canManage || testingId === p.id}
          data-testid={`partner-test-${p.name}`}
        >
          {testingId === p.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PlugZap className="mr-1.5 h-4 w-4" />}
          Test Connection
        </Button>
        {result && (
          <Badge
            className={`border-0 ${result.ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
            data-testid={`partner-test-result-${p.name}`}
            title={result.message}
          >
            {result.ok ? <CheckCircle2 className="mr-1 h-3 w-3" /> : <XCircle className="mr-1 h-3 w-3" />}
            <span className="max-w-[16rem] truncate">{result.message || (result.ok ? "OK" : "Failed")}</span>
          </Badge>
        )}
      </div>
    );
  };

  const handleSavePriority = async (partner, raw) => {
    const value = parseInt(raw, 10);
    if (!Number.isFinite(value) || value < 1) {
      toast.error("Priority must be a whole number ≥ 1");
      return;
    }
    if (value === partner.priority) return;
    setSavingPriorityId(partner.id);
    try {
      await axios.put(`${API}/b2c/admin/partners/${partner.id}`, { priority: value });
      toast.success(`${partner.display_name || aggregatorLabel(partner.name)} priority set to ${value}`);
      await fetchPartners();
    } catch (err) {
      toast.error(errorDetail(err, "Failed to update priority"));
    } finally {
      setSavingPriorityId(null);
    }
  };

  const renderPriority = (p) => (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        min="1"
        defaultValue={p.priority ?? 100}
        key={`prio-${p.id}-${p.priority}`}
        disabled={!canManage || savingPriorityId === p.id}
        onBlur={(e) => handleSavePriority(p, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="h-8 w-16 rounded-md border border-slate-200 px-2 text-sm tabular-nums disabled:opacity-60"
        data-testid={`partner-priority-${p.name}`}
        aria-label={`Priority for ${aggregatorLabel(p.name)}`}
      />
      {savingPriorityId === p.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
    </div>
  );

  const columns = [
    {
      key: "display_name",
      header: "Partner",
      primary: true,
      cell: (p) => (
        <div className="min-w-0">
          <div className="font-medium text-slate-900 truncate">{p.display_name || aggregatorLabel(p.name)}</div>
          <div className="text-xs text-slate-500 capitalize md:hidden">{aggregatorLabel(p.name)}</div>
        </div>
      ),
    },
    { key: "name", header: "Aggregator", hideOnMobile: true, cell: (p) => <span className="text-sm text-slate-700">{aggregatorLabel(p.name)}</span> },
    { key: "environment", header: "Environment", mobileLabel: "Environment", cell: renderEnvironment },
    { key: "status", header: "Status", mobileLabel: "Status", cell: renderStatus },
    { key: "credentials", header: "Credentials", mobileLabel: "Credentials", cell: renderCredentials },
    { key: "token", header: "Token Health", mobileLabel: "Token Health", cell: renderTokenHealth },
    { key: "priority", header: "Priority", mobileLabel: "Priority", cell: renderPriority },
    { key: "actions", header: "", align: "right", isActions: true, cell: renderActions },
  ];

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="B2C Shipping Partners"
          subtitle="White-label aggregators (Shiprocket, iThink) used to quote and ship B2C orders. Admin-only — sellers never see these names."
          actions={
            <Button variant="outline" onClick={fetchPartners} disabled={loading} data-testid="b2c-partners-refresh">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          }
        />

        {/* Env-managed banner */}
        <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="b2c-partners-env-note">
          <FileKey className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
          <div className="text-sm text-slate-600">
            <span className="font-medium text-slate-800">Credentials are managed via environment variables</span> —
            set <code className="rounded bg-slate-200 px-1 py-0.5 text-xs">SHIPROCKET_*</code> /
            <code className="ml-1 rounded bg-slate-200 px-1 py-0.5 text-xs">ITHINK_*</code> in
            <code className="ml-1 rounded bg-slate-200 px-1 py-0.5 text-xs">backend/.env</code> and restart the backend.
            Partners then appear here automatically. Credentials stay read-only here (no keys are entered or shown).
            <span className="font-medium text-slate-800"> Status</span> (enable/disable) and
            <span className="font-medium text-slate-800"> Priority</span> are managed here — each enable/disable is
            recorded with who and when (see <span className="font-medium text-slate-800">History</span>).
          </div>
        </div>

        {/* Priority hint */}
        <p className="text-xs text-slate-500" data-testid="b2c-partners-priority-hint">
          <span className="font-medium text-slate-700">Priority</span> is the tie-breaker at ship time: when two enabled
          partners quote the <span className="font-medium">same cost</span> for a courier, the one with the
          <span className="font-medium"> lower number</span> wins (1 = highest priority). Cheaper price always wins first.
        </p>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center" data-testid="b2c-partners-error">
            <XCircle className="mx-auto mb-3 h-10 w-10 text-red-400" />
            <p className="text-sm font-medium text-red-800">{error}</p>
            <Button variant="outline" className="mt-4" onClick={fetchPartners} data-testid="b2c-partners-retry">
              Retry
            </Button>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={partners}
            rowKey={(p) => p.id}
            loading={loading}
            empty={
              <EmptyState
                icon={Plug}
                title="No aggregators integrated yet"
                description="Add the Shiprocket / iThink credentials to backend/.env (see docs/B2C_STAGE_C_CREDENTIALS.md) and restart the backend — the integrated partners will show up here automatically."
              />
            }
          />
        )}

        {/* Confirm before flipping live status — enabling starts real bookings. */}
        <ConfirmDialog
          open={!!pendingToggle}
          onOpenChange={(v) => !v && setPendingToggle(null)}
          title={
            pendingToggle
              ? `${pendingToggle.next ? "Enable" : "Disable"} ${aggregatorLabel(pendingToggle.partner.name)}?`
              : ""
          }
          description={
            pendingToggle?.next
              ? `Enabling makes EdgeLMS quote and book live shipments through ${aggregatorLabel(
                  pendingToggle.partner.name
                )}. Sellers' rate calculators will make live serviceability calls, and a seller clicking Ship will create a REAL shipment. This writes to the shared production database, so staging honours it immediately too.`
              : pendingToggle
              ? `Disabling stops all quote and ship orchestration through ${aggregatorLabel(
                  pendingToggle.partner.name
                )}. Shipments already booked are unaffected; new quotes and ships will skip this partner.`
              : ""
          }
          confirmLabel={pendingToggle?.next ? "Enable partner" : "Disable partner"}
          tone={pendingToggle?.next ? "destructive" : "default"}
          loading={toggling}
          onConfirm={applyToggle}
        />

        {/* Full enable/disable audit trail for one partner. */}
        <Dialog open={!!historyPartner} onOpenChange={(v) => !v && setHistoryPartner(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-4 w-4 text-slate-400" />
                Status history{historyPartner ? ` — ${aggregatorLabel(historyPartner.name)}` : ""}
              </DialogTitle>
              <DialogDescription>
                Every enable/disable change, newest first — who flipped it and when.
              </DialogDescription>
            </DialogHeader>
            {historyLoading ? (
              <div className="py-8 text-center" data-testid="partner-history-loading">
                <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : historyError ? (
              <div className="py-8 text-center" data-testid="partner-history-error">
                <XCircle className="mx-auto mb-2 h-6 w-6 text-red-400" />
                <p className="text-sm font-medium text-red-700">{historyError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => historyPartner && openHistory(historyPartner)}
                  data-testid="partner-history-retry"
                >
                  <RefreshCw className="mr-2 h-4 w-4" /> Retry
                </Button>
              </div>
            ) : history.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500" data-testid="partner-history-empty">
                No status changes recorded yet.
              </div>
            ) : (
              <ul className="max-h-[55vh] space-y-3 overflow-y-auto pr-1" data-testid="partner-history-list">
                {history.map((h) => (
                  <li
                    key={h.id}
                    className="flex items-start gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0"
                  >
                    <Badge
                      className={`mt-0.5 shrink-0 border-0 ${
                        h.action === "enabled" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {h.action === "enabled" ? "Enabled" : "Disabled"}
                    </Badge>
                    <div className="min-w-0 text-sm">
                      <div className="truncate text-slate-800">
                        {h.actor?.user_name || h.actor?.user_email || "Unknown user"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {fmtDateTime(h.at)}
                        {relativeTime(h.at) ? ` · ${relativeTime(h.at)}` : ""}
                      </div>
                      {h.note && <div className="mt-0.5 text-xs italic text-slate-600">“{h.note}”</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default B2CPartners;
